import React from "react";
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { Client } from "../../../types/client";
import { formatPhoneNumber } from "../../../utils/phoneUtils";
import styles from "./MergeSelectClientsStep.module.css";

interface MergeSelectClientsStepProps {
  records: Client[];
  /** uuid of the record that will survive the merge (the target). */
  winningClientId: string | null;
  onSetWinner: (id: string) => void;
  onRemove: (id: string) => void;
}

const initials = (c: Client): string =>
  `${c.first_name?.[0] ?? ""}${c.last_name?.[0] ?? ""}`.toUpperCase() || "?";

const phone = (c: Client): string =>
  c.area_code || c.phone_number
    ? formatPhoneNumber(`${c.area_code ?? ""}${c.phone_number ?? ""}`)
    : "No phone";

/**
 * Step 1 of the merge wizard. Presents the 2-3 records as tiles and lets the
 * user pick the target profile — the record that survives (keeps its id /
 * payment profile) and absorbs the others. Records can be dropped to a minimum
 * of two.
 */
const MergeSelectClientsStep: React.FC<MergeSelectClientsStepProps> = ({
  records,
  winningClientId,
  onSetWinner,
  onRemove,
}) => {
  const canRemove = records.length > 2;

  return (
    <Stack gap="lg">
      <Box>
        <Title order={3}>Choose Target Profile</Title>
        <Text c="dimmed" size="sm">
          Choose which record to keep as the target profile. All other records
          will be merged into it. You can merge two or three clients at a time.
        </Text>
      </Box>

      <SimpleGrid
        cols={{ base: 1, sm: records.length >= 3 ? 3 : 2 }}
        spacing="md"
        role="radiogroup"
        aria-label="Choose target profile"
      >
        {records.map((rec) => {
          const isTarget = winningClientId === rec.id;
          return (
            <Box
              key={rec.id}
              className={`${styles.tile} ${isTarget ? styles.tilePrimary : ""}`}
              role="radio"
              aria-checked={isTarget}
              tabIndex={0}
              onClick={() => onSetWinner(rec.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSetWinner(rec.id);
                }
              }}
            >
              <Group justify="space-between" mb="xs" wrap="nowrap">
                {isTarget ? (
                  <Badge
                    color="var(--brand-teal)"
                    radius="sm"
                    leftSection={<IconCheck size={12} />}
                  >
                    Target
                  </Badge>
                ) : (
                  <Badge color="gray" variant="light" radius="sm">
                    Merge in
                  </Badge>
                )}
                {canRemove && (
                  <Tooltip label="Remove from merge">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(rec.id);
                      }}
                      aria-label={`Remove ${rec.first_name} ${rec.last_name} from merge`}
                    >
                      <IconX size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>

              <Group gap="sm" wrap="nowrap">
                <Avatar radius="xl" color="var(--brand-teal)" size={44}>
                  {initials(rec)}
                </Avatar>
                <Stack gap={2} style={{ minWidth: 0 }}>
                  <Text fw={600} truncate>
                    {rec.first_name} {rec.last_name}
                  </Text>
                  <Text size="sm" c="dimmed" truncate>
                    {rec.email || "No email"}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {phone(rec)}
                  </Text>
                </Stack>
              </Group>
            </Box>
          );
        })}
      </SimpleGrid>
    </Stack>
  );
};

export default MergeSelectClientsStep;
